import {useMemo, useState} from "react";
import {BaseAlert, BaseBadge, BaseButton, BaseCard} from "@/components";
import type {LotteryCategory, LotteryGame} from "./lottery.types";
import {useLotteryGames} from "./useLotteryGames";

const CATEGORIES: Array<{value: LotteryCategory; label: string}> = [
  {value: "todos", label: "Todos"},
  {value: "animalitos", label: "Animalitos"},
  {value: "triples", label: "Triples"},
  {value: "terminales", label: "Terminales"},
];

const matchesCategory = (game: LotteryGame, category: LotteryCategory): boolean => {
  if (category === "todos") return true;
  const type = game.gameType.toLowerCase();
  if (category === "animalitos") return type === "animals" || type === "animalitos";
  if (category === "triples") return type === "triple" || type === "triplefacil";
  if (category === "terminales") return type.includes("terminal");
  return true;
};

const LotteryPage = () => {
  const {games, isLoading, isError, error, refetch} = useLotteryGames();
  const [category, setCategory] = useState<LotteryCategory>("todos");

  const filteredGames = useMemo(() => games.filter(game => matchesCategory(game, category)), [games, category]);

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8">
      <header className="flex flex-col gap-2">
        <h1 className="text-[var(--font-h1)] font-bold text-[var(--color-text)]">Lotería</h1>
        <p className="text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Elige un juego y participa en los próximos sorteos.
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map(cat => (
          <BaseButton
            key={cat.value}
            type="button"
            variant={category === cat.value ? "primary" : "ghost"}
            size="sm"
            onClick={() => setCategory(cat.value)}
          >
            {cat.label}
          </BaseButton>
        ))}
      </div>

      {isLoading ? (
        <BaseCard
          variant="surface"
          padding="lg"
        >
          <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
            Cargando juegos...
          </p>
        </BaseCard>
      ) : isError ? (
        <BaseAlert
          variant="danger"
          title="No pudimos cargar los juegos"
        >
          <p>{error?.message ?? "Inténtalo nuevamente."}</p>
          <BaseButton
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
          >
            Reintentar
          </BaseButton>
        </BaseAlert>
      ) : filteredGames.length === 0 ? (
        <BaseCard
          variant="surface"
          padding="lg"
        >
          <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
            No hay juegos disponibles en esta categoría.
          </p>
        </BaseCard>
      ) : (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filteredGames.map(game => (
            <li key={game.id}>
              <BaseCard
                variant="surface"
                padding="md"
                interactive
              >
                <div className="flex items-center gap-3">
                  {game.logo ? (
                    <img
                      src={game.logo}
                      alt={game.name}
                      className="h-14 w-14 flex-shrink-0 rounded-lg object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--color-border)] text-[var(--font-body-lg)] font-bold text-[var(--color-text-tertiary)]">
                      {game.name.slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[var(--font-body-lg)] font-semibold text-[var(--color-text)]">
                      {game.name}
                    </p>
                    <p className="truncate text-[var(--font-body)] text-[var(--color-text-tertiary)]">
                      {game.provider}
                    </p>
                    {game.gameType ? (
                      <div className="mt-1">
                        <BaseBadge
                          variant="neutral"
                          size="sm"
                        >
                          {game.gameType}
                        </BaseBadge>
                      </div>
                    ) : null}
                  </div>
                </div>
              </BaseCard>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default LotteryPage;
